import { Fragment, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Check, ChevronDown, ChevronRight, Download, Printer, Undo2, X } from 'lucide-react'
import apiClient, { ApiClientError } from '@/api/client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useCurrency } from '@/contexts/CurrencyContext'
import { useBusinessNameWithFallback } from '@/hooks/useBusinessName'
import { toastHelpers } from '@/lib/toast-helpers'
import { DIVISOR_LABEL, ITEM_KIND_LABEL, METHOD_LABEL, addMonths, downloadCSV, monthLabel, payslipHTML, todayISO } from '@/lib/hr'
import { cn } from '@/lib/utils'
import type { HRPayoutMethod, HRRunDetail, HRRunItem, HRRunLine, HRRunListItem, HRRunStatus, HRSettings } from '@/types/hr'
import { Banner, Empty, Field, NativeSelect, Stat, errMsg } from './shared'
import { PayDialog, ReasonDialog, invalidateHRMoney } from './MoneyDialogs'
import { useHRSettings } from './PeopleTab'
import { hrRole } from './HRPage'

// Payroll (F-HR-N5 / N6). A month goes draft → waiting for approval →
// approved → paid. The draft follows the register live; rules suggest
// bonuses and cuts that someone keeps or removes; the owner approves; then
// people are paid (bank / office cash / wallet) and each payment lands in
// Expenses → Salaries.

const STATUS: Record<HRRunStatus, { label: string; variant: 'neutral-surface' | 'warning-surface' | 'success-surface' }> = {
  draft: { label: 'Draft', variant: 'neutral-surface' },
  submitted: { label: 'Waiting for approval', variant: 'warning-surface' },
  finalized: { label: 'Approved', variant: 'success-surface' },
}

export function PayrollTab({ runId, onRun, onOpenRegister }: { runId?: string; onRun: (id: string | undefined) => void; onOpenRegister: (d: string) => void }) {
  if (runId) return <RunView id={runId} onBack={() => onRun(undefined)} onOpenRegister={onOpenRegister} />
  return <RunList onOpen={onRun} />
}

function monthsAvailable(settings?: HRSettings, taken: string[] = []): string[] {
  const now = todayISO().slice(0, 7)
  const start = settings?.start_month || addMonths(now, -11)
  const out: string[] = []
  for (let m = now; m >= start && out.length < 24; m = addMonths(m, -1)) if (!taken.includes(m)) out.push(m)
  return out
}

function RunList({ onOpen }: { onOpen: (id: string) => void }) {
  const qc = useQueryClient()
  const { formatCurrency } = useCurrency()
  const settings = useHRSettings()
  const q = useQuery({ queryKey: ['hr', 'runs'], queryFn: () => apiClient.hrListRuns() })
  const runs = (q.data?.data as HRRunListItem[] | undefined) ?? []
  const options = monthsAvailable(settings, runs.map((r) => r.month))
  const [month, setMonth] = useState('')
  const pick = month || options[0] || ''
  const create = useMutation({
    mutationFn: () => apiClient.hrCreateRun(pick),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['hr', 'runs'] })
      if (res.data?.id) onOpen(res.data.id)
    },
    onError: (err) => {
      const id = ((err as ApiClientError).payload as { id?: string } | undefined)?.id
      if (id) onOpen(id)
      else toastHelpers.error('Couldn’t start payroll', errMsg(err))
    },
  })
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <p className="max-w-xl text-sm text-muted-foreground">
          One payroll a month. It’s worked out from the register, pay, advances and your rules — and stays up to date until it’s sent for approval.
        </p>
        {options.length > 0 && (
          <div className="flex items-end gap-2">
            <Field label="Month">
              <NativeSelect value={pick} onChange={(e) => setMonth(e.target.value)} className="h-9">
                {options.map((m) => (
                  <option key={m} value={m}>
                    {monthLabel(m)}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Button size="sm" className="h-9" onClick={() => create.mutate()} disabled={!pick || create.isPending}>
              Start payroll
            </Button>
          </div>
        )}
      </div>
      {q.isLoading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : runs.length === 0 ? (
        <Empty>No payroll yet. Start one for the month that just ended.</Empty>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-4 py-2 font-medium">Month</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 text-right font-medium">People</th>
                <th className="px-4 py-2 text-right font-medium">Gross</th>
                <th className="px-4 py-2 text-right font-medium">Cuts &amp; recoveries</th>
                <th className="px-4 py-2 text-right font-medium">Net</th>
                <th className="px-4 py-2 text-right font-medium">Paid</th>
                <th className="px-4 py-2 text-right font-medium">Left to pay</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {runs.map((r) => (
                <tr key={r.id} className="cursor-pointer hover:bg-muted/30" onClick={() => onOpen(r.id)}>
                  <td className="px-4 py-2.5 font-medium">{monthLabel(r.month)}</td>
                  <td className="px-4 py-2.5">
                    <Badge variant={STATUS[r.status].variant}>{STATUS[r.status].label}</Badge>
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{r.totals.people}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{formatCurrency(r.totals.gross)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">
                    −{formatCurrency(r.totals.absence_deduction + r.totals.deductions + r.totals.advance_recovery)}
                  </td>
                  <td className="px-4 py-2.5 text-right font-medium tabular-nums">{formatCurrency(r.totals.net)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{r.status === 'finalized' ? formatCurrency(r.totals.paid) : '—'}</td>
                  <td className={cn('px-4 py-2.5 text-right tabular-nums', r.status === 'finalized' && r.totals.unpaid > 0 && 'font-medium')}>
                    {r.status === 'finalized' ? formatCurrency(r.totals.unpaid) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function daysSummary(l: HRRunLine): string {
  const d = l.detail
  const parts = [`${d.worked_days} worked`]
  if (d.absent_days) parts.push(`${d.absent_days} absent`)
  if (d.late_count) parts.push(`${d.late_count} late`)
  if (d.paid_leave_days) parts.push(`${d.paid_leave_days} leave`)
  if (d.unpaid_leave_days) parts.push(`${d.unpaid_leave_days} unpaid leave`)
  if (d.employed_days < d.days_in_period) parts.push(`${d.employed_days}/${d.days_in_period} days employed`)
  return parts.join(' · ')
}

function RunView({ id, onBack, onOpenRegister }: { id: string; onBack: () => void; onOpenRegister: (d: string) => void }) {
  const qc = useQueryClient()
  const { formatCurrency } = useCurrency()
  const business = useBusinessNameWithFallback()
  const { isAdmin } = hrRole()
  const [open, setOpen] = useState<Record<string, boolean>>({})
  const [dialog, setDialog] = useState<null | 'return' | 'reopen' | 'payall'>(null)
  const [removing, setRemoving] = useState<HRRunItem | null>(null)
  const [paying, setPaying] = useState<HRRunLine | null>(null)
  const q = useQuery({ queryKey: ['hr', 'run', id], queryFn: () => apiClient.hrGetRun(id) })
  const d = q.data?.data as HRRunDetail | undefined
  const peopleQ = useQuery({ queryKey: ['hr', 'employees', 'all'], queryFn: () => apiClient.hrListEmployees('all') })
  const byId = useMemo(() => Object.fromEntries(((peopleQ.data?.data ?? []) as { id: string }[]).map((p) => [p.id, p])), [peopleQ.data])

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['hr', 'run', id] })
    qc.invalidateQueries({ queryKey: ['hr', 'runs'] })
    qc.invalidateQueries({ queryKey: ['hr', 'overview'] })
  }
  const act = <T,>(fn: () => Promise<T>, ok: string) =>
    ({
      mutationFn: fn,
      onSuccess: () => {
        toastHelpers.success(ok)
        refresh()
        invalidateHRMoney(qc)
        setDialog(null)
      },
      onError: (err: unknown) => toastHelpers.error('Couldn’t do that', errMsg(err)),
    }) as const
  const keepAll = useMutation(act(() => apiClient.hrKeepAll(id), 'Kept every suggestion'))
  const submit = useMutation(act(() => apiClient.hrSubmitRun(id), 'Sent for approval'))
  const approve = useMutation(act(() => apiClient.hrApproveRun(id), 'Payroll approved'))
  const sendBack = useMutation({ ...act(() => Promise.resolve(), 'Sent back'), mutationFn: (note: string) => apiClient.hrReturnRun(id, note) })
  const reopen = useMutation({ ...act(() => Promise.resolve(), 'Payroll reopened'), mutationFn: (reason: string) => apiClient.hrReopenRun(id, reason) })
  const decide = useMutation({
    mutationFn: (v: { item: HRRunItem; state: 'kept' | 'removed' | 'suggested'; note?: string }) => apiClient.hrDecideRunItem(id, v.item.id, { state: v.state, note: v.note }),
    onSuccess: () => {
      refresh()
      setRemoving(null)
    },
    onError: (err) => toastHelpers.error('Couldn’t save', errMsg(err)),
  })

  if (q.isLoading || !d) return <div className="text-sm text-muted-foreground">Loading…</div>
  const { run, totals, lines } = d
  const draft = run.status === 'draft'
  const final = run.status === 'finalized'

  const print = (only?: HRRunLine) => {
    const w = window.open('', '_blank')
    if (!w) {
      toastHelpers.error('Allow pop-ups to print payslips')
      return
    }
    w.document.write(payslipHTML({ business, run, lines: only ? [only] : lines, money: formatCurrency }))
    w.document.close()
    w.focus()
    setTimeout(() => w.print(), 300)
  }
  const exportCSV = () =>
    downloadCSV(`payroll-${run.month}.csv`, [
      ['Name', 'Department', 'Pay type', 'Rate', 'Worked', 'Absent', 'Late', 'Paid leave', 'Unpaid leave', 'Gross', 'Absence cut', 'Bonuses', 'Deductions', 'Advance recovered', 'Net', 'Paid', 'Paid by', 'Account'],
      ...lines.map((l) => {
        const p = byId[l.employee_id] as { bank_name?: string; account_number?: string } | undefined
        return [
          l.detail.full_name,
          l.detail.department,
          l.detail.pay_type,
          l.detail.rate,
          l.detail.worked_days,
          l.detail.absent_days,
          l.detail.late_count,
          l.detail.paid_leave_days,
          l.detail.unpaid_leave_days,
          l.gross,
          l.absence_deduction,
          l.bonuses,
          l.deductions,
          l.advance_recovery,
          l.net,
          l.paid,
          METHOD_LABEL[l.detail.payout_method] ?? '',
          [p?.bank_name, p?.account_number].filter(Boolean).join(' '),
        ]
      }),
    ])

  const firstUnmarked = lines.flatMap((l) => l.detail.unmarked_dates ?? []).sort()[0]

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            All months
          </Button>
          <h2 className="text-lg font-semibold">{monthLabel(run.month)}</h2>
          <Badge variant={STATUS[run.status].variant}>{STATUS[run.status].label}</Badge>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => print()}>
            <Printer className="mr-1.5 h-4 w-4" />
            Payslips
          </Button>
          <Button variant="outline" size="sm" onClick={exportCSV}>
            <Download className="mr-1.5 h-4 w-4" />
            Export
          </Button>
          {draft && totals.pending_suggestions > 0 && (
            <Button variant="outline" size="sm" onClick={() => keepAll.mutate()} disabled={keepAll.isPending}>
              Keep all {totals.pending_suggestions} suggestions
            </Button>
          )}
          {draft && (
            <Button size="sm" onClick={() => submit.mutate()} disabled={submit.isPending}>
              Send for approval
            </Button>
          )}
          {run.status === 'submitted' && (
            <>
              <Button variant="outline" size="sm" onClick={() => setDialog('return')}>
                <Undo2 className="mr-1.5 h-4 w-4" />
                Send back
              </Button>
              {d.can_approve && (
                <Button size="sm" onClick={() => approve.mutate()} disabled={approve.isPending}>
                  <Check className="mr-1.5 h-4 w-4" />
                  Approve
                </Button>
              )}
            </>
          )}
          {final && totals.unpaid > 0 && (
            <Button size="sm" onClick={() => setDialog('payall')}>
              Pay everyone ({formatCurrency(totals.unpaid)})
            </Button>
          )}
          {final && d.can_reopen && (
            <Button variant="outline" size="sm" onClick={() => setDialog('reopen')}>
              Reopen
            </Button>
          )}
        </div>
      </div>

      {draft && run.returned_note && <Banner tone="warn">Sent back: {run.returned_note}</Banner>}
      {draft && totals.unmarked_people > 0 && (
        <Banner tone="warn">
          {totals.unmarked_people} {totals.unmarked_people === 1 ? 'person has' : 'people have'} days not marked — they’re counted as worked for now.{' '}
          {firstUnmarked && (
            <button className="font-medium underline" onClick={() => onOpenRegister(firstUnmarked)}>
              Open the register
            </button>
          )}
        </Banner>
      )}
      {totals.negative_people > 0 && <Banner tone="danger">Cuts are more than the month’s pay for {totals.negative_people} people — remove or lower a line.</Banner>}
      {run.status === 'submitted' && (
        <Banner>
          Sent by {run.submitted_by ?? 'someone'}
          {run.submitted_at ? ` on ${new Date(run.submitted_at).toLocaleDateString('en-GB')}` : ''}.{' '}
          {d.can_approve ? 'Check it and approve.' : d.approve_blocked_reason || 'Waiting for the owner.'} The month’s attendance is locked meanwhile.
        </Banner>
      )}
      {final && (
        <Banner>
          Approved by {run.finalized_by ?? 'the owner'}
          {run.finalized_at ? ` on ${new Date(run.finalized_at).toLocaleDateString('en-GB')}` : ''}. Payments go to Expenses → Salaries.
        </Banner>
      )}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Stat label="People" value={totals.people} />
        <Stat label="Gross pay" value={formatCurrency(totals.gross)} />
        <Stat label="Cuts" value={`−${formatCurrency(totals.absence_deduction + totals.deductions)}`} hint={`${formatCurrency(totals.absence_deduction)} absences`} />
        <Stat label="Bonuses" value={`+${formatCurrency(totals.bonuses)}`} hint={totals.advance_recovery ? `−${formatCurrency(totals.advance_recovery)} advances` : undefined} />
        <Stat label={final ? 'Net · left to pay' : 'Net pay'} value={formatCurrency(totals.net)} hint={final ? `${formatCurrency(totals.unpaid)} left to pay` : undefined} />
      </div>

      {lines.length === 0 ? (
        <Empty>Nobody was employed this month.</Empty>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
              <tr>
                <th className="w-6 px-2 py-2" />
                <th className="px-3 py-2 font-medium">Person</th>
                <th className="px-3 py-2 text-right font-medium">Pay</th>
                <th className="px-3 py-2 text-right font-medium">Absence</th>
                <th className="px-3 py-2 text-right font-medium">Bonus</th>
                <th className="px-3 py-2 text-right font-medium">Cuts</th>
                <th className="px-3 py-2 text-right font-medium">Advance</th>
                <th className="px-3 py-2 text-right font-medium">Net</th>
                {final && <th className="px-3 py-2 text-right font-medium">Left to pay</th>}
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => {
                const pending = l.items.filter((i) => i.state === 'suggested').length
                const isOpen = open[l.employee_id] ?? false
                return (
                  <Fragment key={l.employee_id}>
                    <tr className="cursor-pointer border-t hover:bg-muted/30" onClick={() => setOpen((o) => ({ ...o, [l.employee_id]: !isOpen }))}>
                      <td className="px-2 py-2.5 text-muted-foreground">{isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2 font-medium">
                          {l.detail.full_name}
                          {pending > 0 && <Badge variant="warning-surface">{pending} to decide</Badge>}
                          {l.unmarked_days > 0 && <Badge variant="warning-surface">{l.unmarked_days} not marked</Badge>}
                          {l.detail.no_rate && <Badge variant="danger-surface">No pay set</Badge>}
                        </div>
                        <div className="text-xs text-muted-foreground">{daysSummary(l)}</div>
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{formatCurrency(l.gross)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-muted-foreground">{l.absence_deduction ? `−${formatCurrency(l.absence_deduction)}` : '—'}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{l.bonuses ? `+${formatCurrency(l.bonuses)}` : '—'}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-muted-foreground">{l.deductions ? `−${formatCurrency(l.deductions)}` : '—'}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-muted-foreground">{l.advance_recovery ? `−${formatCurrency(l.advance_recovery)}` : '—'}</td>
                      <td className={cn('px-3 py-2.5 text-right font-semibold tabular-nums', l.raw_net < 0 && 'text-red-700 dark:text-red-300')}>{formatCurrency(l.net)}</td>
                      {final && (
                        <td className="px-3 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                          {l.unpaid > 0 ? (
                            <Button size="sm" variant="outline" onClick={() => setPaying(l)}>
                              Pay {formatCurrency(l.unpaid)}
                            </Button>
                          ) : (
                            <span className="text-xs text-muted-foreground">{l.net > 0 ? 'Paid' : '—'}</span>
                          )}
                        </td>
                      )}
                    </tr>
                    {isOpen && (
                      <tr className="bg-muted/20">
                        <td />
                        <td colSpan={final ? 8 : 7} className="px-3 pb-4 pt-1">
                          <LineDetail
                            line={l}
                            draft={draft}
                            busy={decide.isPending}
                            onKeep={(item) => decide.mutate({ item, state: 'kept' })}
                            onUndo={(item) => decide.mutate({ item, state: 'suggested' })}
                            onRemove={(item) => (item.source === 'rule' ? setRemoving(item) : decide.mutate({ item, state: 'removed' }))}
                            onPrint={() => print(l)}
                            runId={id}
                            onAdded={refresh}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <ReasonDialog
        open={!!removing}
        onClose={() => setRemoving(null)}
        title="Remove this line?"
        description={removing ? `${removing.reason}. Say why it doesn’t apply this month — it stays on record.` : ''}
        action="Remove"
        busy={decide.isPending}
        onConfirm={(note) => removing && decide.mutate({ item: removing, state: 'removed', note })}
      />
      <ReasonDialog
        open={dialog === 'return'}
        onClose={() => setDialog(null)}
        title="Send back for changes?"
        description="It goes back to draft and the month’s attendance unlocks. Say what needs fixing."
        action="Send back"
        busy={sendBack.isPending}
        onConfirm={(note) => sendBack.mutate(note)}
      />
      {isAdmin && (
        <ReasonDialog
          open={dialog === 'reopen'}
          onClose={() => setDialog(null)}
          title="Reopen this payroll?"
          description="Its salaries, bonuses and cuts come off everyone’s balance and it goes back to draft. Only possible before anyone is paid."
          action="Reopen"
          destructive
          busy={reopen.isPending}
          onConfirm={(reason) => reopen.mutate(reason)}
        />
      )}
      <PayAllDialog open={dialog === 'payall'} onClose={() => setDialog(null)} runId={id} unpaid={totals.unpaid} onDone={refresh} />
      <PayDialog
        open={!!paying}
        onClose={() => setPaying(null)}
        person={paying ? { ...(byId[paying.employee_id] ?? {}), id: paying.employee_id, full_name: paying.detail.full_name, payout_method: paying.detail.payout_method } : null}
        runId={id}
        maxAmount={paying?.unpaid ?? 0}
        title={paying ? `Pay ${paying.detail.full_name} · ${monthLabel(run.month)}` : undefined}
      />
    </div>
  )
}

function LineDetail({
  line,
  draft,
  busy,
  onKeep,
  onUndo,
  onRemove,
  onPrint,
  runId,
  onAdded,
}: {
  line: HRRunLine
  draft: boolean
  busy: boolean
  onKeep: (i: HRRunItem) => void
  onUndo: (i: HRRunItem) => void
  onRemove: (i: HRRunItem) => void
  onPrint: () => void
  runId: string
  onAdded: () => void
}) {
  const { formatCurrency } = useCurrency()
  const [kind, setKind] = useState<'bonus' | 'deduction'>('bonus')
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const add = useMutation({
    mutationFn: () => apiClient.hrAddRunItem(runId, { employee_id: line.employee_id, kind, amount: Number(amount), reason }),
    onSuccess: () => {
      setAmount('')
      setReason('')
      onAdded()
    },
    onError: (err) => toastHelpers.error('Couldn’t add', errMsg(err)),
  })
  const d = line.detail
  return (
    <div className="space-y-3">
      <div className="text-xs text-muted-foreground">
        {d.pay_type === 'daily'
          ? `Daily wage ${formatCurrency(d.rate)}`
          : `Salary ${formatCurrency(d.rate)} a month · one day = ${formatCurrency(d.per_day)} (${DIVISOR_LABEL[d.divisor]}${d.divisor !== '30' ? `: ${d.divisor_days}` : ''})`}
        {d.rate_changed ? ' · pay changed this month (split by days)' : ''} · {d.off_days} off · {d.holiday_days} holidays
        {d.half_days ? ` · ${d.half_days} half days` : ''}
        {d.unmarked_dates?.length ? ` · not marked: ${d.unmarked_dates.map((x) => Number(x.slice(8))).join(', ')}` : ''}
      </div>
      {line.items.length > 0 && (
        <div className="divide-y rounded-lg border bg-background">
          {line.items.map((i) => (
            <div key={i.id} className={cn('flex flex-wrap items-center justify-between gap-2 px-3 py-2', i.state === 'removed' && 'opacity-50')}>
              <div className="min-w-0">
                <div className="text-sm">
                  <span className="font-medium">{ITEM_KIND_LABEL[i.kind]}</span> · {i.reason}
                </div>
                <div className="text-xs text-muted-foreground">
                  {i.source === 'manual' ? 'Added by hand' : i.source === 'rule' ? 'From a rule' : 'Automatic'}
                  {i.state === 'suggested' ? ' · waiting for a decision' : i.state === 'removed' ? ` · removed${i.decision_note ? `: ${i.decision_note}` : ''}` : ''}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="tabular-nums">
                  {i.kind === 'bonus' || i.kind === 'leave_encashment' ? '+' : '−'}
                  {formatCurrency(i.amount)}
                </span>
                {draft && i.state === 'suggested' && (
                  <>
                    <Button size="sm" variant="outline" className="h-8" onClick={() => onKeep(i)} disabled={busy}>
                      <Check className="mr-1 h-3.5 w-3.5" />
                      Keep
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8" onClick={() => onRemove(i)} disabled={busy}>
                      <X className="mr-1 h-3.5 w-3.5" />
                      Remove
                    </Button>
                  </>
                )}
                {draft && i.state !== 'suggested' && i.source !== 'manual' && (
                  <Button size="sm" variant="ghost" className="h-8" onClick={() => onUndo(i)} disabled={busy}>
                    Undo
                  </Button>
                )}
                {draft && i.source === 'manual' && (
                  <Button size="sm" variant="ghost" className="h-8" onClick={() => onRemove(i)} disabled={busy}>
                    Delete
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-end gap-2">
        {draft && (
          <>
            <NativeSelect value={kind} onChange={(e) => setKind(e.target.value as 'bonus' | 'deduction')} className="h-9">
              <option value="bonus">Bonus</option>
              <option value="deduction">Deduction</option>
            </NativeSelect>
            <Input type="number" min={0} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount" className="h-9 w-28" />
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="What for (prints on the payslip)" className="h-9 w-72" />
            <Button size="sm" variant="outline" className="h-9" onClick={() => add.mutate()} disabled={!(Number(amount) > 0) || !reason.trim() || add.isPending}>
              Add line
            </Button>
          </>
        )}
        <Button size="sm" variant="ghost" className="ml-auto h-9" onClick={onPrint}>
          <Printer className="mr-1.5 h-4 w-4" />
          Payslip
        </Button>
      </div>
    </div>
  )
}

function PayAllDialog({ open, onClose, runId, unpaid, onDone }: { open: boolean; onClose: () => void; runId: string; unpaid: number; onDone: () => void }) {
  const qc = useQueryClient()
  const { formatCurrency } = useCurrency()
  const [paidOn, setPaidOn] = useState(todayISO())
  const [method, setMethod] = useState<HRPayoutMethod | ''>('')
  const [reference, setReference] = useState('')
  const pay = useMutation({
    mutationFn: () => apiClient.hrPayAll(runId, { paid_on: paidOn, method, reference }),
    onSuccess: (res) => {
      toastHelpers.success(`Paid ${res.data?.paid ?? 0} people`, `${formatCurrency(res.data?.total ?? 0)} added to Expenses → Salaries`)
      invalidateHRMoney(qc)
      onDone()
      onClose()
    },
    onError: (err) => toastHelpers.error('Couldn’t record payments', errMsg(err)),
  })
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Pay everyone</DialogTitle>
          <DialogDescription>Records {formatCurrency(unpaid)} paid to everyone still owed on this payroll, from the office or owner — not the till.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Paid on">
            <Input type="date" value={paidOn} max={todayISO()} onChange={(e) => setPaidOn(e.target.value)} />
          </Field>
          <Field label="Paid by">
            <NativeSelect value={method} onChange={(e) => setMethod(e.target.value as HRPayoutMethod | '')}>
              <option value="">Each person’s usual way</option>
              {(Object.keys(METHOD_LABEL) as HRPayoutMethod[]).map((m) => (
                <option key={m} value={m}>
                  {METHOD_LABEL[m]}
                </option>
              ))}
            </NativeSelect>
          </Field>
        </div>
        <Field label="Reference" hint="Optional — e.g. the bulk transfer number">
          <Input value={reference} onChange={(e) => setReference(e.target.value)} />
        </Field>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => pay.mutate()} disabled={pay.isPending}>
            Record payments
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
